import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Parse une date Pocketbase de façon sécurisée
 * Pocketbase retourne: "2024-01-15 14:30:00.000Z" ou "2024-01-15T14:30:00.000Z"
 */
export function parseDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;
  
  try {
    // Pocketbase peut retourner "2024-01-15 14:30:00.000Z" (avec espace)
    // On remplace l'espace par T pour être ISO compliant
    const normalized = dateStr.replace(' ', 'T');
    const date = parseISO(normalized);
    
    if (isValid(date)) {
      return date;
    }
    
    // Fallback: essayer new Date()
    const fallback = new Date(dateStr);
    if (isValid(fallback)) {
      return fallback;
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Formate une date pour l'affichage
 * Ex: "13 fév 2026 à 23:15"
 */
export function formatDate(dateStr: string | undefined | null, pattern = "d MMM yyyy 'à' HH:mm"): string {
  const date = parseDate(dateStr);
  if (!date) return '—';
  
  try {
    return format(date, pattern, { locale: fr });
  } catch {
    return '—';
  }
}

/**
 * Formate une date courte
 * Ex: "13/02/2026"
 */
export function formatDateShort(dateStr: string | undefined | null): string {
  return formatDate(dateStr, 'dd/MM/yyyy');
}

/**
 * Formate une date relative
 * Ex: "il y a 3 jours"
 */
export function formatRelative(dateStr: string | undefined | null): string {
  const date = parseDate(dateStr);
  if (!date) return '—';
  
  try {
    return formatDistanceToNow(date, { addSuffix: true, locale: fr });
  } catch {
    return '—';
  }
}

/**
 * Formate un numéro de téléphone français
 */
export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  
  // Nettoyer
  const cleaned = phone.replace(/\s+/g, '').replace(/[.-]/g, '');
  
  // Format: 06 12 34 56 78
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  
  // Format international: +33 6 12 34 56 78
  if (cleaned.startsWith('+33') && cleaned.length === 12) {
    const national = '0' + cleaned.slice(3);
    return formatPhone(national);
  }
  
  return phone;
}

/**
 * Initiales d'un nom
 */
export function getInitials(name: string | undefined | null): string {
  if (!name) return '?';
  
  return name
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() || '')
    .join('');
}

/**
 * Classe utilitaire pour merger des classes conditionnelles
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}
